import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import Flatpickr from 'react-flatpickr';
import { format } from 'date-fns';
import HospitalBranding from './HospitalBranding';
import HomeFooter from './HomeFooter';
import PageShell from './PageShell';

const preventInvalidNumberKeys = (event) => {
    if (['e', 'E', '+', '-'].includes(event.key)) {
        event.preventDefault();
    }
};

const DateTimeField = ({ formData, formErrors, onChangeDateTime }) => (
    <div className="d-flex flex-column mb-3">
        <label htmlFor="flatpickr-datetime" className="form-control-label">
            Consultation Date &amp; Time<span className="text-danger"> *</span>
        </label>
        <Flatpickr
            className="form-control"
            placeholder="YYYY-MM-DD HH:MM"
            id="flatpickr-datetime"
            name="DateAndTime"
            value={formData.DateAndTime}
            onChange={onChangeDateTime}
            options={{ enableTime: true, dateFormat: 'Y-m-d H:i', time_24hr: false, minDate: format(new Date(), 'yyyy-MM-dd') }}
        />
        {formErrors.DateAndTime && <p className="text-danger m-0">{formErrors.DateAndTime}</p>}
    </div>
);

const CurrencyFields = ({ fieldName, formData, onChangeHandler }) => (
    <>
        {fieldName && (
            <div className="d-flex flex-column mb-3">
                <label className="form-control-label">Discount Percentage (<span className="text-danger">%</span>)</label>
                <input
                    type="number"
                    name={fieldName}
                    className="form-control"
                    placeholder="Enter Discount Percentage"
                    value={formData[fieldName] ?? ''}
                    min={0}
                    max={100}
                    onChange={onChangeHandler}
                    onKeyDown={preventInvalidNumberKeys}
                />
            </div>
        )}
        {['PaidAmount', 'TotalAmount'].map((name) => (
            <div className="d-flex flex-column mb-3" key={name}>
                <label className="form-control-label">{name === 'PaidAmount' ? 'Paid Amount' : 'Total Amount'} (<i className="bi bi-currency-rupee text-danger"></i>)</label>
                <input
                    type="number"
                    name={name}
                    className="form-control"
                    placeholder={`Enter ${name === 'PaidAmount' ? 'Paid' : 'Total'} Amount`}
                    value={formData[name] ?? ''}
                    min={0}
                    max={999999}
                    onChange={onChangeHandler}
                    onKeyDown={preventInvalidNumberKeys}
                />
            </div>
        ))}
    </>
);

const FormActions = ({ handleCancel, handleReset, submitLoading }) => (
    <div className="text-center">
        <button type="button" className="btn btn-secondary me-1" onClick={handleCancel}>Cancel</button>
        <button type="button" className="btn btn-danger me-1" onClick={handleReset}>Reset</button>
        <button type="submit" className="btn btn-success" style={{ width: '80px', height: '40px' }} disabled={submitLoading}>
            {submitLoading ? <span className="spinner-border text-white" role="status" aria-hidden="true"></span> : 'Submit'}
        </button>
    </div>
);

const BookingFormScreen = ({
    eligibilityMessage,
    formData,
    formErrors,
    formSuccessMessage,
    handleCancel,
    handleReset,
    handleSubmit,
    hospitalImage,
    hospitalLogo,
    hospitalName,
    logo,
    onChangeDateTime,
    onChangeHandler,
    service,
    serviceTypes,
    submitLoading,
    windowSize,
}) => {
    const isConsultation = service === 'consultation';
    const isLab = service === 'lab';
    const title = isConsultation ? 'Free Booking Consultation' : isLab ? 'Booking Lab Investigation' : 'Pharmacy Discount';

    return (
        <PageShell windowSize={windowSize}>
            <div style={{ position: 'sticky', top: '30px', display: 'flex', justifyContent: 'flex-start', width: '100%' }}>
                <button
                    type="button"
                    aria-label="Back to services"
                    style={{
                        backgroundColor: '#0E94C3', color: '#fff', border: 'none', borderRadius: '50%', width: '40px', height: '40px',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px',
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)', cursor: 'pointer', zIndex: 1000,
                    }}
                    onClick={handleCancel}
                >
                    <FontAwesomeIcon icon={faArrowLeft} />
                </button>
            </div>

            <HospitalBranding hospitalName={hospitalName} hospitalLogo={hospitalLogo} hospitalImage={hospitalImage} />
            <div className="p-3 text-start">
                <h4 className="mb-5 text-center">
                    {title} for <br />
                    <span style={{ color: '#0E94C3' }} className="fs-5 text-success">{formData.FullName}</span>
                </h4>
                <form onSubmit={handleSubmit}>
                    <div className="d-flex flex-column align-items-start" style={{ minWidth: '350px' }}>
                        {(isConsultation || isLab) && (
                            <DateTimeField formData={formData} formErrors={formErrors} onChangeDateTime={onChangeDateTime} />
                        )}

                        {isConsultation && (
                            <>
                                <div className="d-flex flex-column mb-3">
                                    <label className="form-control-label">Doctor Name<span className="text-danger"> *</span></label>
                                    <input type="text" name="DoctorName" className="form-control" placeholder="Enter Doctor Name" value={formData.DoctorName ?? ''} onChange={onChangeHandler} />
                                    {formErrors.DoctorName && <p className="text-danger m-0">{formErrors.DoctorName}</p>}
                                </div>
                                <div className="d-flex flex-column mb-3">
                                    <label className="form-control-label">Service Type<span className="text-danger"> *</span></label>
                                    <select name="ServiceType" className="form-control" value={formData.ServiceType ?? ''} onChange={onChangeHandler}>
                                        <option value="">--- SELECT ---</option>
                                        {serviceTypes?.map((type) => <option key={type.HospitalServicesId} value={type.HospitalServicesId}>{type.ServiceName}</option>)}
                                    </select>
                                    {formErrors.ServiceType && <p className="text-danger m-0">{formErrors.ServiceType}</p>}
                                </div>
                            </>
                        )}

                        {!isConsultation && <CurrencyFields fieldName={isLab ? 'LabPercentage' : 'PharmacyPercentage'} formData={formData} onChangeHandler={onChangeHandler} />}
                    </div>
                    <FormActions handleCancel={handleCancel} handleReset={handleReset} submitLoading={submitLoading} />
                    {formSuccessMessage && <p className="text-success text-center">{formSuccessMessage}</p>}
                    {eligibilityMessage && <p className="text-danger text-center">{eligibilityMessage}</p>}
                </form>
            </div>
            <HomeFooter logo={logo} />
        </PageShell>
    );
};

export default BookingFormScreen;
